import {
  forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import i18n from '../../../common/i18n';
import SectionHeading from './SectionHeading';
import FaqItem from '../commons/FaqItem';
import getHelpFaqGroups from '../commons/helpData';
import {
  HELP_SCROLL_MARGIN_TOP, cardShadowOnWhite, faqGroupShadow, helpColors,
} from '../style/helpStyles';

const HelpFaqs = forwardRef(({ selectedCategory, isMobile, showAllGroups }, ref) => {
  const [expandedItem, setExpandedItem] = useState(null);
  const groupRefs = useRef({});
  const groups = getHelpFaqGroups();

  useImperativeHandle(ref, () => ({
    scrollToGroup: (categoryId) => {
      groupRefs.current[categoryId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  }), []);

  useEffect(() => {
    if (!showAllGroups) {
      setExpandedItem(null);
    }
  }, [selectedCategory, showAllGroups]);

  const visibleGroups = showAllGroups
    ? groups
    : [groups.find((group) => group.id === selectedCategory) || groups[0]];

  return (
    <Box component="section">
      <SectionHeading
        eyebrow={i18n.faq}
        title={i18n.helpFaqTitle}
        titleVariant={isMobile ? 'h5' : 'h4'}
        mb={3}
        align="left"
        eyebrowSize={14}
        titleSize={isMobile ? 28 : 40}
      />
      {visibleGroups.map((group) => (
        <Box
          key={group.id}
          ref={(node) => { groupRefs.current[group.id] = node; }}
          sx={{
            backgroundColor: helpColors.white,
            borderRadius: '22px',
            padding: isMobile ? '6px 18px' : '10px 26px',
            boxShadow: isMobile ? faqGroupShadow : cardShadowOnWhite,
            scrollMarginTop: HELP_SCROLL_MARGIN_TOP,
            '& + &': { marginTop: '12px' },
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: '-0.01em',
              color: helpColors.pink,
              padding: '14px 0 6px',
              textAlign: 'left',
            }}
          >
            {group.title}
          </Typography>
          {group.items.map((item, index) => (
            <FaqItem
              key={item.id}
              question={item.question}
              answer={item.answer}
              expanded={expandedItem === item.id}
              onChange={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
              isLast={index === group.items.length - 1}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
});

HelpFaqs.displayName = 'HelpFaqs';

HelpFaqs.propTypes = {
  selectedCategory: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  showAllGroups: PropTypes.bool,
};

HelpFaqs.defaultProps = {
  isMobile: false,
  showAllGroups: false,
};

export default HelpFaqs;
